import React, { forwardRef } from 'react'
import PropTypes from 'prop-types'
import { useCallback, useEffect, useRef } from 'sb-hooks'
import autosize from 'autosize'


const UPDATE = 'autosize:update'
const DESTROY = 'autosize:destroy'
const RESIZED = 'autosize:resized'


const Textarea = ({ value, onResize, forwardedRef, ...rest }) => {
  const textareaRef = useRef()

  const setRef = useCallback((node) => {
    textareaRef.current = node

    if (typeof forwardedRef === 'function') {
      forwardedRef(node)
    }
    else if (forwardedRef) {
      forwardedRef.current = node
    }
  }, [ forwardedRef ])

  const dispatchEvent = useCallback((EVENT_TYPE, defer) => {
    const event = document.createEvent('Event')

    event.initEvent(EVENT_TYPE, true, false)

    const dispatch = () => {
      if (textareaRef.current) {
        textareaRef.current.dispatchEvent(event)
      }
    }

    if (defer) {
      setTimeout(dispatch, 0)
    }
    else {
      dispatch()
    }
  }, [])

  useEffect(() => {
    const textareaEl = textareaRef.current

    autosize(textareaEl)

    if (typeof onResize === 'function') {
      textareaEl.addEventListener(RESIZED, onResize)
    }

    return () => {
      if (typeof onResize === 'function') {
        textareaEl.removeEventListener(RESIZED, onResize)
      }

      dispatchEvent(DESTROY)
    }
  }, [ dispatchEvent, onResize ])

  // update from outside
  const previousValueRef = useRef(value)

  useEffect(() => {
    if (previousValueRef.current !== value) {
      dispatchEvent(UPDATE, true)
      previousValueRef.current = value
    }
  }, [ dispatchEvent, value ])

  return (
    <textarea
      ref={setRef}
      value={value}
      {...rest}
    />
  )
}


Textarea.propTypes = {
  onResize: PropTypes.func,
}

Textarea.defaultProps = {
  rows: 1,
}


export default forwardRef((props, ref) => (
  <Textarea {...props} forwardedRef={ref}  />
))
